import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, ExternalLink, Edit3, CreditCard, Building2, Users, MapPinned, ClipboardCheck, Activity, ShieldCheck, Mail, Calendar, Loader2, Plus, Power, MoreHorizontal, Trash2, Download } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
  BackendPlanTier,
  BackendTenantStatus,
  BackendUserRole,
  PlatformTenantDetail,
  PlatformUser,
  RoleMetadata,
  UpdateTenantBody,
  platformTenantsApi,
  platformUsersApi,
  rolesApi,
  statusToLabel,
  tierToLabel,
  userStatusToLabel,
} from "@/lib/platform-api";
import { ApiError } from "@/lib/api";
import { validateRequired } from "@/lib/form-validation";
import { BulkUserImportDialog } from "@/components/BulkUserImportDialog";
import { InviteLinkDialog } from "@/components/InviteLinkDialog";
import { assignableRoles } from "@/lib/roles";

const PLAN_OPTIONS: { value: BackendPlanTier; label: string }[] = [
  { value: "BASIC", label: "Basic" },
  { value: "PRO", label: "Pro" },
  { value: "PREMIUM", label: "Premium" },
  { value: "ENTERPRISE", label: "Enterprise" },
];

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();

  const [tenant, setTenant] = useState<PlatformTenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [roles, setRoles] = useState<RoleMetadata[]>([]);

  const [reloadKey, setReloadKey] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<{
    name: string; industry: string; region: string; status: BackendTenantStatus;
    primaryContact: string; contactEmail: string; renewalDate: string;
  } | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  /** Surfaced invite URL for the InviteLinkDialog. */
  const [inviteLink, setInviteLink] = useState<{
    name: string;
    email: string;
    url: string;
    variant: "new" | "reset";
  } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    platformTenantsApi
      .get(id)
      .then((res) => { if (!cancelled) setTenant(res); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else toast.error("Failed to load tenant", { description: errMsg(e) });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setUsersLoading(true);
    platformUsersApi
      .list({ tenantId: id, pageSize: 100 })
      .then((res) => { if (!cancelled) setUsers(res.data); })
      .catch((e) => {
        if (!cancelled) toast.error("Failed to load users", { description: errMsg(e) });
      })
      .finally(() => { if (!cancelled) setUsersLoading(false); });
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  useEffect(() => {
    rolesApi.list().then(setRoles).catch(() => { /* non-fatal */ });
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Loading tenant...</p>
      </div>
    );
  }

  if (notFound || !tenant) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Building2 className="h-12 w-12 text-muted-foreground" />
        <h2 className="mt-4 font-display text-xl font-semibold">Tenant not found</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/platform/tenants")}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to tenants
        </Button>
      </div>
    );
  }

  const planLabel = tierToLabel(tenant.plan.tier);
  const statusLabel = statusToLabel(tenant.status);

  const handleImpersonate = async () => {
    const res = await startImpersonation({ tenantId: tenant.id, tenantName: tenant.name });
    if (!res.ok) {
      toast.error("Could not start impersonation", { description: res.error });
      return;
    }
    toast.success(`Now viewing ${tenant.name}`);
    navigate("/");
  };

  const handleChangePlan = async (plan: BackendPlanTier) => {
    try {
      await platformTenantsApi.changePlan(tenant.id, plan);
      toast.success(`Plan changed to ${tierToLabel(plan)}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not change plan", { description: errMsg(e) });
    }
  };

  const openEdit = () => {
    if (!tenant) return;
    setEditForm({
      name: tenant.name,
      industry: tenant.industry ?? "",
      region: tenant.region ?? "",
      status: tenant.status,
      primaryContact: tenant.primaryContact ?? "",
      contactEmail: tenant.contactEmail ?? "",
      renewalDate: tenant.renewalDate ? tenant.renewalDate.slice(0, 10) : "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!tenant || !editForm) return;
    if (!editForm.name.trim()) { toast.error("Tenant name is required"); return; }
    setEditSubmitting(true);
    try {
      const body: UpdateTenantBody = {
        name: editForm.name.trim(),
        industry: editForm.industry.trim() || undefined,
        region: editForm.region.trim() || undefined,
        status: editForm.status,
        primaryContact: editForm.primaryContact.trim() || undefined,
        contactEmail: editForm.contactEmail.trim() || undefined,
        renewalDate: editForm.renewalDate ? new Date(editForm.renewalDate).toISOString() : undefined,
      };
      await platformTenantsApi.update(tenant.id, body);
      toast.success("Tenant updated");
      setEditOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not update tenant", { description: errMsg(e) });
    } finally {
      setEditSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!tenant) return;
    setDeleteSubmitting(true);
    try {
      await platformTenantsApi.remove(tenant.id);
      toast.success(`${tenant.name} deleted`);
      navigate("/platform/tenants");
    } catch (e) {
      toast.error("Could not delete tenant", { description: errMsg(e) });
      setDeleteSubmitting(false);
    }
  };

  /**
   * Full data export for this tenant (KPTM BQ G2/G3). Super-admin only and run
   * on the customer's request — there is deliberately no tenant-side button.
   * The download is a complete dump of their personal data, so it is
   * audit-logged server-side.
   */
  const handleExport = async () => {
    if (!tenant) return;
    setExporting(true);
    try {
      await platformTenantsApi.exportData(tenant.id, tenant.code);
      toast.success("Export downloaded", {
        description: "Complete tenant data in NDJSON, with a data dictionary in the first line.",
      });
    } catch (e) {
      toast.error("Export failed", {
        description: e instanceof ApiError ? e.message : "Please try again.",
      });
    } finally {
      setExporting(false);
    }
  };

  const handleSuspendToggle = async () => {
    try {
      if (tenant.status === "SUSPENDED") await platformTenantsApi.reactivate(tenant.id);
      else await platformTenantsApi.suspend(tenant.id);
      toast.success(`${tenant.name} ${tenant.status === "SUSPENDED" ? "reactivated" : "suspended"}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Action failed", { description: errMsg(e) });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/platform/tenants")}><ArrowLeft className="mr-2 h-4 w-4" /> All tenants</Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={openEdit}><Edit3 className="mr-1.5 h-4 w-4" /> Edit</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm"><CreditCard className="mr-1.5 h-4 w-4" /> Change plan</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {PLAN_OPTIONS.filter(p => p.value !== tenant.plan.tier).map(p => (
                <DropdownMenuItem key={p.value} onClick={() => handleChangePlan(p.value)}>{p.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}
                  title="Full data export — run on the customer's request (audit-logged)">
            {exporting
              ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              : <Download className="mr-1.5 h-4 w-4" />}
            Export data
          </Button>
          <Button variant="outline" size="sm" onClick={handleSuspendToggle}>
            <Power className="mr-1.5 h-4 w-4" /> {tenant.status === "SUSPENDED" ? "Reactivate" : "Suspend"}
          </Button>
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete
          </Button>
          <Button size="sm" className="bg-gradient-to-r from-accent to-primary hover:opacity-90" onClick={handleImpersonate}>
            <ExternalLink className="mr-1.5 h-4 w-4" /> Open Tenant View
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-lg font-bold text-white">
              {tenant.code.slice(0, 3)}
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-display text-xl font-bold">{tenant.name}</h2>
                <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">{planLabel}</Badge>
                <Badge variant="outline" className={
                  statusLabel === "Active" ? "border-success/30 bg-success/10 text-success" :
                  statusLabel === "Suspended" ? "border-destructive/30 bg-destructive/10 text-destructive" :
                  "border-warning/30 bg-warning/10 text-warning"
                }>{statusLabel}</Badge>
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                {tenant.industry ?? "—"}{tenant.region ? ` · ${tenant.region}` : ""} · {tenant.supportTier} support
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {tenant.primaryContact && <span className="flex items-center gap-1.5"><Users className="h-3 w-3" /> {tenant.primaryContact}</span>}
                {tenant.contactEmail && <span className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {tenant.contactEmail}</span>}
                <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Created {new Date(tenant.createdAt).toLocaleDateString()}</span>
                {tenant.renewalDate && <span className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> Renews {new Date(tenant.renewalDate).toLocaleDateString()}</span>}
              </div>
            </div>
          </div>

          {/* Licence utilisation against the contractual cap (FEAT-2026-018).
              Shown only when the tenant actually has a limit — an uncapped
              tenant has nothing to be a percentage of. */}
          {tenant.licence?.limit != null && (
            <div className="mt-5 rounded-lg border p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  User licences
                </span>
                <span
                  className={`text-sm font-semibold ${
                    (tenant.licence.percentage ?? 0) >= 100
                      ? "text-destructive"
                      : (tenant.licence.percentage ?? 0) >= 90
                        ? "text-warning"
                        : "text-foreground"
                  }`}
                >
                  {tenant.licence.used.toLocaleString()} / {tenant.licence.limit.toLocaleString()}
                  <span className="ml-1 text-muted-foreground">({tenant.licence.percentage}%)</span>
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className={`h-full rounded-full transition-all ${
                    (tenant.licence.percentage ?? 0) >= 100
                      ? "bg-destructive"
                      : (tenant.licence.percentage ?? 0) >= 90
                        ? "bg-warning"
                        : "bg-primary"
                  }`}
                  style={{ width: `${Math.min(100, tenant.licence.percentage ?? 0)}%` }}
                />
              </div>
              {(tenant.licence.percentage ?? 0) >= 90 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {(tenant.licence.percentage ?? 0) >= 100
                    ? "At or over the contracted limit — additional users may be chargeable."
                    : "Approaching the contracted limit. The tenant's administrators have been notified."}
                </p>
              )}
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              { l: "Active Users", v: tenant.counts.activeUsers.toLocaleString(), i: Users },
              { l: "Employees", v: tenant.counts.employees.toLocaleString(), i: Users },
              { l: "Sites", v: tenant.counts.sites, i: MapPinned },
              { l: "Today's Records", v: tenant.counts.attendanceToday.toLocaleString(), i: Activity },
              { l: "Pending Requests", v: tenant.counts.pendingRequests, i: ClipboardCheck },
            ].map(s => (
              <div key={s.l} className="rounded-lg border bg-secondary/30 p-3">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  <s.i className="h-3 w-3" /> {s.l}
                </div>
                <div className="mt-1 font-display text-lg font-bold">{s.v}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <Card>
            <CardHeader className="flex-row items-center justify-between border-b pb-3">
              <CardTitle className="text-base">Users in {tenant.name}</CardTitle>
              <div className="flex items-center gap-2">
                <BulkUserImportDialog
                  title={`Bulk import to ${tenant.name}`}
                  onImport={(rows) => platformUsersApi.bulkImport(tenant.id, rows)}
                  onCompleted={() => setReloadKey((k) => k + 1)}
                />
                <AddUserDialog
                  tenantId={tenant.id}
                  tenantName={tenant.name}
                  roles={roles}
                  onCreated={() => setReloadKey((k) => k + 1)}
                  onInviteGenerated={(name, email, url) =>
                    setInviteLink({ name, email, url, variant: "new" })
                  }
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                    <TableHead>Status</TableHead><TableHead>Last login</TableHead><TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersLoading && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading...</TableCell></TableRow>}
                  {!usersLoading && users.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No users yet</TableCell></TableRow>}
                  {!usersLoading && users.map(u => (
                    <TableRow key={u.id}>
                      <TableCell className="text-sm font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                      <TableCell className="text-sm">{u.roleLabel ?? u.roleType}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          u.status === "ACTIVE" ? "border-success/30 bg-success/10 text-success" :
                          u.status === "PENDING" ? "border-warning/30 bg-warning/10 text-warning" :
                          "border-destructive/30 bg-destructive/10 text-destructive"
                        }>{userStatusToLabel(u.status)}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={async () => {
                              try {
                                if (u.status === "DISABLED") await platformUsersApi.enable(u.id);
                                else await platformUsersApi.disable(u.id);
                                toast.success(`${u.name} ${u.status === "DISABLED" ? "enabled" : "disabled"}`);
                                setReloadKey((k) => k + 1);
                              } catch (e) { toast.error("Action failed", { description: errMsg(e) }); }
                            }}>
                              <Power className="mr-2 h-3.5 w-3.5" /> {u.status === "DISABLED" ? "Enable" : "Disable"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Billing & Renewal</CardTitle><CardDescription>Plan: {planLabel}</CardDescription></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Current plan</span><span className="font-medium">{planLabel}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Renewal date</span><span className="font-medium">{tenant.renewalDate ? new Date(tenant.renewalDate).toLocaleDateString() : "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Support tier</span><span className="font-medium">{tenant.supportTier}</span></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Tenant settings</CardTitle><CardDescription>Managed by tenant admins. Platform overrides coming soon.</CardDescription></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {tenant.settings ? Object.entries(tenant.settings).slice(0, 10).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b pb-2 last:border-b-0">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-mono text-xs">{v === null ? "—" : String(v)}</span>
                </div>
              )) : <p className="text-muted-foreground">No tenant settings on record.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) setEditForm(null); setEditOpen(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit tenant</DialogTitle>
            <DialogDescription>Update organization details and status.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="grid gap-3 py-2">
              <div className="grid gap-1.5">
                <Label>Tenant name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label>Industry</Label><Input value={editForm.industry} onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Region</Label><Input value={editForm.region} onChange={(e) => setEditForm({ ...editForm, region: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v as BackendTenantStatus })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="TRIAL">Trial</SelectItem>
                      <SelectItem value="SUSPENDED">Suspended</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Renewal date</Label>
                  <Input type="date" value={editForm.renewalDate} onChange={(e) => setEditForm({ ...editForm, renewalDate: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label>Primary contact</Label><Input value={editForm.primaryContact} onChange={(e) => setEditForm({ ...editForm, primaryContact: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Contact email</Label><Input type="email" value={editForm.contactEmail} onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSubmitting}>Cancel</Button>
            <Button onClick={submitEdit} disabled={editSubmitting}>
              {editSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {tenant.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the tenant and all of its data — users, sites, employees, attendance records, and requests. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {inviteLink && (
        <InviteLinkDialog
          open={!!inviteLink}
          onClose={() => setInviteLink(null)}
          recipientName={inviteLink.name}
          recipientEmail={inviteLink.email}
          inviteUrl={inviteLink.url}
          variant={inviteLink.variant}
        />
      )}
    </div>
  );
}

function AddUserDialog({
  tenantId,
  tenantName,
  roles,
  onCreated,
  onInviteGenerated,
}: {
  tenantId: string;
  tenantName: string;
  roles: RoleMetadata[];
  onCreated: () => void;
  onInviteGenerated: (name: string, email: string, url: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // No department field here: tenant departments are a managed, tenant-scoped
  // list (ADR-0008) — the tenant's own HR assigns them from the Users page.
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    roleType: "TENANT_OWNER" as BackendUserRole,
  });
  const assignable = assignableRoles(roles.filter(r => r.scope === "tenant"));

  const submit = async () => {
    const missing = validateRequired([
      { label: "Name", value: form.name },
      { label: "Email", value: form.email },
    ]);
    if (missing) {
      toast.error(missing);
      return;
    }
    setSubmitting(true);
    try {
      const res = await platformUsersApi.create({
        tenantId,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        roleType: form.roleType,
      });
      setOpen(false);
      setForm({ name: "", email: "", phone: "", roleType: "TENANT_OWNER" });
      onCreated();
      if (res.linkedToExisting) {
        toast.success(`${res.name} added to ${tenantName}`, {
          description: `Linked to their existing account (${res.email}). They sign in with their current password.`,
          duration: 8000,
        });
      } else if (res.inviteUrl) {
        onInviteGenerated(res.name, res.email, res.inviteUrl);
      } else {
        toast.success(`${res.name} added to ${tenantName}`, {
          description: `Invitation email sent to ${res.email}.`,
          duration: 6000,
        });
      }
    } catch (e) {
      toast.error("Could not create user", { description: errMsg(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-gradient-to-r from-accent to-primary hover:opacity-90">
          <Plus className="mr-1.5 h-4 w-4" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add user to {tenantName}</DialogTitle>
          <DialogDescription>
            They'll receive an email invitation to set up their account.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid gap-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></div>
          <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@tenant.com" /></div>
          <div className="grid gap-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60..." /></div>
          <div className="grid gap-1.5">
            <Label>Role</Label>
            <Select value={form.roleType} onValueChange={(v) => setForm({ ...form, roleType: v as BackendUserRole })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(assignable.length > 0 ? assignable : [
                  { key: "TENANT_OWNER" as BackendUserRole, label: "Tenant Owner", scope: "tenant" as const, description: "", assignableByTenantOwner: false },
                  { key: "HR_MANAGER" as BackendUserRole, label: "HR Manager", scope: "tenant" as const, description: "", assignableByTenantOwner: true },
                  { key: "OPERATIONS_ADMIN" as BackendUserRole, label: "Operations Admin", scope: "tenant" as const, description: "", assignableByTenantOwner: true },
                  { key: "SITE_ADMIN" as BackendUserRole, label: "Site Admin", scope: "tenant" as const, description: "", assignableByTenantOwner: true },
                  { key: "EMPLOYEE" as BackendUserRole, label: "Employee", scope: "tenant" as const, description: "", assignableByTenantOwner: true },
                ]).map(r => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unexpected error";
}
